import { type IntegrationsCapabilityModeEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  type SyncStatusColumn,
  type SyncStatusColumnItem,
  type SyncStatusColumnValue,
  usePrivateConditionalIntegrationsSyncContext,
} from 'modules/integrations/hooks/SyncContext'
import { MergeableStatuses, mergeStatuses } from 'modules/integrations/lib/syncableStatus'

type GetStatusesFromSyncable = (syncable: SyncStatusColumnItem) => MergeableStatuses | undefined

interface UseSyncColumnOptions {
  getStatusesFromSyncable: GetStatusesFromSyncable
  disabled?: boolean
}

export function useSyncColumn(options: UseSyncColumnOptions): SyncStatusColumn[] {
  const context = usePrivateConditionalIntegrationsSyncContext(options.disabled)
  const syncEnabled = context?.syncEnabled ?? false

  const FILE_MODE: IntegrationsCapabilityModeEnum = 'file'
  const isExportMode = context?.capabilityMode === FILE_MODE

  const syncableStateStatus = (
    syncable: SyncStatusColumnItem
  ): SyncStatusColumnValue['value']['status'] | null => {
    if (!syncEnabled || !syncable) return null

    const statuses = options.getStatusesFromSyncable(syncable)

    return mergeStatuses(statuses)
  }

  if (!syncEnabled) return []

  const exportLabel = i18n.t('integrations.columns.export_status_title')
  const syncLabel = i18n.t('integrations.columns.sync_status_title')

  const exportStatusTooltipTexts: Record<SyncStatusColumnValue['value']['status'], string> = {
    failed: i18n.t('integrations.columns.export_status.failed'),
    outdated: i18n.t('integrations.columns.export_status.outdated'),
    partiallySynced: i18n.t('integrations.columns.export_status.partially_synced'),
    pending: i18n.t('integrations.columns.export_status.pending'),
    synced: i18n.t('integrations.columns.export_status.synced'),
    syncing: i18n.t('integrations.columns.export_status.syncing'),
  }

  const syncStatusTooltipTexts: Record<SyncStatusColumnValue['value']['status'], string> = {
    failed: i18n.t('integrations.columns.sync_status.failed'),
    outdated: i18n.t('integrations.columns.sync_status.outdated'),
    partiallySynced: i18n.t('integrations.columns.sync_status.partially_synced'),
    pending: i18n.t('integrations.columns.sync_status.pending'),
    synced: i18n.t('integrations.columns.sync_status.synced'),
    syncing: i18n.t('integrations.columns.sync_status.syncing'),
  }

  const tooltipTexts = isExportMode ? exportStatusTooltipTexts : syncStatusTooltipTexts

  return [
    {
      label: isExportMode ? exportLabel : syncLabel,
      render: (item: SyncStatusColumnItem) => {
        const status = syncableStateStatus(item)
        // if status is null means the item is not syncable
        // so we return undefined to not display the column icon
        if (status === null) return undefined

        return {
          type: 'syncStatus' as const,
          value: {
            status,
            tooltip: tooltipTexts[status],
          },
        }
      },
    },
  ]
}
